import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Eye, TrendingUp, Monitor, Smartphone, Tablet, Globe } from "lucide-react";
import { api } from "../../services/api";

const PAGE_LABELS = {
    "/":                    "Domovská stránka",
    "/referencie":          "Referencie",
    "/technika":            "Technika",
    "/prenajom":            "Prenájom",
    "/vizualizacie":        "3D Vizualizácie",
};

function pageLabel(path) {
    if (PAGE_LABELS[path]) return PAGE_LABELS[path];
    if (path.startsWith("/sluzby/")) return `Služba: ${path.replace("/sluzby/", "")}`;
    if (path.startsWith("/referencie/")) return "Detail referencie";
    if (path.startsWith("/vizualizacie/")) return "Detail vizualizácie";
    return path;
}

function StatCard({ label, visits, unique, icon: Icon, color }) {
    return (
        <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</span>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}20` }}>
                    <Icon className="w-4 h-4" style={{ color }} />
                </div>
            </div>
            <div className="flex items-end gap-4">
                <div>
                    <div className="font-heading text-3xl font-black text-white">{visits.toLocaleString("sk")}</div>
                    <div className="font-mono text-[10px] text-zinc-600 mt-0.5">zobrazení</div>
                </div>
                <div className="mb-1">
                    <div className="font-heading text-lg font-bold" style={{ color }}>{unique.toLocaleString("sk")}</div>
                    <div className="font-mono text-[10px] text-zinc-600">unikátnych</div>
                </div>
            </div>
        </div>
    );
}

function CustomTooltip({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div className="bg-nova-surface border border-white/10 rounded-lg px-4 py-3 text-xs shadow-xl">
            <div className="font-mono text-zinc-400 mb-2">{label}</div>
            <div className="flex gap-4">
                <div><span className="text-nova-cyan font-bold">{payload[0]?.value}</span> <span className="text-zinc-500">zobrazení</span></div>
                <div><span className="text-nova-purple font-bold">{payload[1]?.value}</span> <span className="text-zinc-500">unikátnych</span></div>
            </div>
        </div>
    );
}

function DeviceBar({ devices }) {
    const total = Object.values(devices).reduce((a, b) => a + b, 0) || 1;
    const items = [
        { key: "desktop", label: "Desktop",  icon: Monitor,    color: "#0099ff" },
        { key: "mobile",  label: "Mobil",    icon: Smartphone, color: "#a855f7" },
        { key: "tablet",  label: "Tablet",   icon: Tablet,     color: "#f59e0b" },
    ];
    return (
        <div className="space-y-3">
            {items.map(({ key, label, icon: Icon, color }) => {
                const count = devices[key] || 0;
                const pct = Math.round((count / total) * 100);
                return (
                    <div key={key}>
                        <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                                <Icon className="w-3.5 h-3.5" style={{ color }} />
                                <span className="text-sm text-zinc-300">{label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-mono text-xs text-zinc-400">{count.toLocaleString("sk")}</span>
                                <span className="font-mono text-xs font-bold" style={{ color }}>{pct}%</span>
                            </div>
                        </div>
                        <div className="h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: color }} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

function formatDate(iso) {
    const [, m, d] = iso.split("-");
    return `${parseInt(d)}.${parseInt(m)}.`;
}

export default function AdminStats() {
    const [data, setData]     = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.getVisitorSummary()
            .then(setData)
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="space-y-4 animate-pulse">
            <div className="grid grid-cols-3 gap-4">
                {[1, 2, 3].map(n => <div key={n} className="h-32 bg-nova-surface/30 rounded-2xl" />)}
            </div>
            <div className="h-64 bg-nova-surface/30 rounded-2xl" />
        </div>
    );

    if (!data) return (
        <div className="text-zinc-500 text-sm font-mono">Nepodarilo sa načítať štatistiky.</div>
    );

    const chartData = data.daily.map(d => ({ ...d, date: formatDate(d.date) }));

    return (
        <div className="space-y-6 max-w-5xl">
            {/* Header */}
            <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-nova-cyan mb-1">/ Návštevnosť</div>
                <h1 className="font-heading text-2xl font-black tracking-tight">Štatistiky webu</h1>
                <p className="text-zinc-500 text-sm mt-1">Posledných 30 dní · bez cookies · bez externých služieb</p>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <StatCard label="Dnes"          visits={data.today.visits} unique={data.today.unique} icon={Eye}        color="#0099ff" />
                <StatCard label="Tento týždeň"  visits={data.week.visits}  unique={data.week.unique}  icon={TrendingUp} color="#a855f7" />
                <StatCard label="Posledných 30 dní" visits={data.month.visits} unique={data.month.unique} icon={Users}  color="#22c55e" />
            </div>

            {/* Chart */}
            <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-6">
                <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-5">/ Návštevy za posledných 30 dní</h2>
                <ResponsiveContainer width="100%" height={220}>
                    <AreaChart data={chartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <defs>
                            <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#0099ff" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#0099ff" stopOpacity={0} />
                            </linearGradient>
                            <linearGradient id="gUnique" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="#a855f7" stopOpacity={0.25} />
                                <stop offset="95%" stopColor="#a855f7" stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.04)" />
                        <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} interval={4} />
                        <YAxis tick={{ fill: "#71717a", fontSize: 10, fontFamily: "monospace" }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="visits" stroke="#0099ff" strokeWidth={2} fill="url(#gVisits)" name="Zobrazenia" dot={false} />
                        <Area type="monotone" dataKey="unique" stroke="#a855f7" strokeWidth={1.5} fill="url(#gUnique)" name="Unikátni" dot={false} strokeDasharray="4 2" />
                    </AreaChart>
                </ResponsiveContainer>
                <div className="flex items-center gap-6 mt-3">
                    <div className="flex items-center gap-2"><div className="w-5 h-0.5 bg-nova-cyan rounded" /><span className="text-zinc-500 text-xs">Zobrazenia</span></div>
                    <div className="flex items-center gap-2"><div className="w-5 h-0.5 bg-nova-purple rounded border-dashed" style={{ borderTop: "1.5px dashed #a855f7", background: "none" }} /><span className="text-zinc-500 text-xs">Unikátni návštevníci</span></div>
                </div>
            </div>

            {/* Bottom row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

                {/* Top pages */}
                <div className="lg:col-span-2 bg-nova-surface/30 border border-white/8 rounded-2xl p-6">
                    <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">/ Najnavštevovanejšie stránky</h2>
                    {data.top_pages.length === 0 ? (
                        <p className="text-zinc-600 text-sm">Zatiaľ žiadne dáta.</p>
                    ) : (
                        <div className="space-y-2">
                            {data.top_pages.map((p, i) => {
                                const max = data.top_pages[0]?.count || 1;
                                const pct = Math.round((p.count / max) * 100);
                                return (
                                    <div key={p.path} className="flex items-center gap-3 group">
                                        <span className="font-mono text-[10px] text-zinc-700 w-4 shrink-0">{i + 1}</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <span className="text-sm text-white truncate">{pageLabel(p.path)}</span>
                                                <span className="font-mono text-xs text-zinc-400 ml-3 shrink-0">{p.count.toLocaleString("sk")}</span>
                                            </div>
                                            <div className="h-1 bg-white/5 rounded-full overflow-hidden">
                                                <div className="h-full bg-nova-cyan/60 rounded-full" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Right column */}
                <div className="space-y-4">
                    {/* Device breakdown */}
                    <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-5">
                        <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">/ Zariadenia</h2>
                        <DeviceBar devices={data.devices} />
                    </div>

                    {/* Top referrers */}
                    <div className="bg-nova-surface/30 border border-white/8 rounded-2xl p-5">
                        <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-4">/ Zdroje návštev</h2>
                        {data.top_referrers.length === 0 ? (
                            <p className="text-zinc-600 text-xs">Zatiaľ len priame návštevy.</p>
                        ) : (
                            <div className="space-y-2.5">
                                {data.top_referrers.map(r => (
                                    <div key={r.domain} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Globe className="w-3 h-3 text-zinc-600 shrink-0" />
                                            <span className="text-sm text-zinc-300 truncate">{r.domain}</span>
                                        </div>
                                        <span className="font-mono text-xs text-zinc-500 ml-2 shrink-0">{r.count}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
